import * as c from "@yahshi/contracts";
import type { MembershipApi, AssetPage } from "./api";
// Shared local data is supplied by the unified development adapter. Provider
// payments/refunds still require the real transactional API.
export interface DevelopmentAssetData {
  pools: c.AssetPool[];
  products: c.AssetProduct[];
  bindings: c.AssetBinding[];
  accounts(): c.AssetAccount[];
  ledger(): c.AssetLedger[];
  adjust(input: c.AssetAdjustment): c.AssetAccount;
}
export function createDevelopmentMembershipApi(
  getApps: () => Promise<{ id: string; name: string; status: string }[]>,
  data?: DevelopmentAssetData,
): MembershipApi {
  const virtualConfigs: c.VirtualConfig[] = [],
    virtualMappings: c.VirtualMapping[] = [];
  const pools: c.AssetPool[] = data?.pools ?? [],
    products: c.AssetProduct[] = data?.products ?? [],
    bindings: c.AssetBinding[] = data?.bindings ?? [];
  const audits: {
    id: string;
    action: string;
    actor: string;
    targetId: string;
    reason: string;
    createdAt: string;
  }[] = [];
  const mutations = new Map<string, { input: string; result: unknown }>();
  const mutate = <T>(key: string, input: unknown, fn: () => T): T => {
    const prior = mutations.get(key),
      serialized = JSON.stringify(input);
    if (prior) {
      if (prior.input !== serialized) throw new Error("操作标识已用于不同请求");
      return structuredClone(prior.result) as T;
    }
    const result = fn();
    mutations.set(key, { input: serialized, result: structuredClone(result) });
    return structuredClone(result);
  };
  const page = <T extends object>(
    rows: T[],
    q: c.AssetQuery = {},
  ): AssetPage<T> => {
    const value = c.membershipQuerySchema.parse(q);
    const filtered = rows.filter((row) => {
      const data = row as Record<string, unknown>;
      return (
        (!value.poolId ||
          data.poolId === value.poolId ||
          data.id === value.poolId) &&
        (!value.referenceId || data.referenceId === value.referenceId) &&
        (!value.userId || data.userId === value.userId) &&
        (!value.appId || (typeof data.appId === "string" ? data.appId === value.appId : bindings.some((b) => b.appId === value.appId && b.poolId === data.poolId))) &&
        JSON.stringify(row)
          .toLowerCase()
          .includes((value.keyword ?? "").toLowerCase())
      );
    });
    return {
      items: structuredClone(
        filtered.slice(
          (value.page - 1) * value.pageSize,
          value.page * value.pageSize,
        ),
      ),
      total: filtered.length,
    };
  };
  const audit = (action: string, targetId: string, reason: string) =>
    audits.unshift({
      id: crypto.randomUUID(),
      action,
      targetId,
      reason,
      actor: "本地管理员",
      createdAt: new Date().toISOString(),
    });
  return {
    eventHealth: async () => { throw new Error("通知监控需要连接测试服务"); },
    eventList: async () => { throw new Error("虚拟支付通知事件需要连接测试服务"); },
    eventDetail: async () => { throw new Error("虚拟支付通知事件需要连接测试服务"); },
    eventRetry: async () => { throw new Error("虚拟支付通知事件需要连接测试服务"); },
    statementList: async () => { throw new Error("官方下载任务需要连接测试服务"); },
    statementDetail: async () => { throw new Error("官方下载任务需要连接测试服务"); },
    statementCreate: async () => { throw new Error("官方下载任务需要连接测试服务"); },
    statementAction: async () => { throw new Error("官方下载任务需要连接测试服务"); },
    statementLink: async () => { throw new Error("官方下载任务需要连接测试服务"); },
    virtualWorkerStatus: async () => { throw new Error("后台任务状态需要连接测试服务，本地演示没有真实运行记录"); },
    reconciliationList: async (q = {}) => { c.virtualReconciliationQuerySchema.parse(q); return { items: [], total: 0 }; },
    reconciliationCreate: async () => { throw new Error("平台账务核对需要连接测试服务，本地演示不生成核对结果"); },
    reconciliationDetail: async () => { throw new Error("本地演示没有真实核对批次"); },
    reconciliationItems: async () => { throw new Error("批次明细需要连接测试服务，本地演示没有真实快照"); },
    reconciliationResolve: async () => { throw new Error("差异处置需要连接测试服务"); },
    virtualConfig: async () =>
      structuredClone({ configs: virtualConfigs, mappings: virtualMappings }),
    saveVirtualConfig: async (input) => {
      const value = c.virtualConfigInputSchema.parse(input);
      const stored = { ...value };
      for (const [plain, ref] of [["appKey", "appKeyRef"], ["callbackToken", "callbackTokenRef"], ["callbackAesKey", "callbackAesKeyRef"]] as const) {
        if (stored[plain]) stored[ref] = `stored://virtual/${crypto.randomUUID()}`;
        delete stored[plain];
      }
      return mutate(stored.mutationId, stored, () => {
        const prior = virtualConfigs.find((x) => x.appId === value.appId);
        if ((prior?.version ?? 0) !== value.expectedVersion)
          throw new Error("配置已更新，请刷新后重试");
        const result = c.virtualConfigSchema.parse({
          ...stored,
          version: value.expectedVersion + 1,
        });
        if (prior) Object.assign(prior, result);
        else virtualConfigs.push(result);
        audit("virtual_config", value.appId, value.reason);
        return result;
      });
    },
    saveVirtualMapping: async (input) => {
      const value = c.virtualMappingInputSchema.parse(input);
      return mutate(value.mutationId, value, () => {
        const prior = virtualMappings.find(
          (x) => x.appId === value.appId && x.productId === value.productId,
        );
        if ((prior?.version ?? 0) !== value.expectedVersion)
          throw new Error("配置已更新，请刷新后重试");
        const result = c.virtualMappingSchema.parse({
          ...value,
          version: value.expectedVersion + 1,
        });
        if (prior) Object.assign(prior, result);
        else virtualMappings.push(result);
        audit("virtual_mapping", value.productId, value.reason);
        return result;
      });
    },
    virtualSubscribe: async () => {
      throw new Error("会员订阅需要连接现网虚拟支付，本地演示不调用微信");
    },
    syncVirtual: async () => {
      throw new Error("查询微信订单需要连接真实测试服务");
    },
    refundVirtual: async () => {
      throw new Error("退款需要连接真实测试服务");
    },
    pools: async (q) => page(pools, q),
    products: async (q) => page(products, q),
    bindings: async (q) =>
      page(
        (await getApps()).filter((app) => !q?.appId || app.id === q.appId).flatMap((app) =>
          (["membership", "points", "item"] as const).map((kind) => {
            const current = bindings.find(
              (b) => b.appId === app.id && b.kind === kind,
            );
            return {
              appId: app.id,
              appName: app.name,
              kind,
              poolId: current?.poolId ?? null,
              poolName:
                pools.find((p) => p.id === current?.poolId)?.name ?? null,
              poolNameUg:
                pools.find((p) => p.id === current?.poolId)?.nameUg ?? null,
              version: current?.version ?? 0,
            };
          }),
        ),
        q,
      ),
    accounts: async (q) => page(data?.accounts() ?? [], q),
    purchases: async (q = {}) => { c.purchaseQuerySchema.parse(q); return { items: [], total: 0 }; },
    purchaseTrace: async () => { throw new Error("本地演示没有真实购买记录，请连接测试服务后查询"); },
    ledger: async (q) => page(data?.ledger() ?? [], q),
    audit: async (q) => page(audits, q),
    savePool: async (id, input) => {
      const v = c.assetPoolInputSchema.parse(input);
      return mutate(v.mutationId, { id, v }, () => {
        const old = pools.find((p) => p.id === id);
        if ((old?.version ?? 0) !== v.expectedVersion || (id && !old))
          throw new Error("记录已更新，请刷新后重试");
        if (old && !!old.sandbox !== v.sandbox)
          throw new Error("不能变更空间测试环境");
        if (old && old.kind !== v.kind) throw new Error("不能变更空间类型");
        const row: c.AssetPool = {
          id: id ?? crypto.randomUUID(),
          name: v.name,
          nameUg: v.nameUg,
          kind: v.kind,
          enabled: v.enabled,
          sandbox: v.sandbox,
          version: (old?.version ?? 0) + 1,
          createdAt: old?.createdAt ?? new Date().toISOString(),
        };
        if (old) pools.splice(pools.indexOf(old), 1, row);
        else pools.unshift(row);
        audit("pool", row.id, v.reason);
        return row;
      });
    },
    saveProduct: async (id, input) => {
      const v = c.assetProductInputSchema.parse(input);
      return mutate(v.mutationId, { id, v }, () => {
        if (
          !pools.some(
            (p) => p.id === v.poolId && p.kind === v.kind && p.enabled,
          )
        )
          throw new Error("请选择有效权益空间");
        const old = products.find((p) => p.id === id);
        if ((old?.version ?? 0) !== v.expectedVersion || (id && !old))
          throw new Error("记录已更新，请刷新后重试");
        if (old && (old.poolId !== v.poolId || old.assetKey !== v.assetKey))
          throw new Error("已创建商品不能变更资产归属");
        const { reason } = v;
        const spec = c.assetProductSchema
          .omit({ id: true, version: true, createdAt: true })
          .parse(v);
        const row: c.AssetProduct = {
          ...spec,
          id: id ?? crypto.randomUUID(),
          version: (old?.version ?? 0) + 1,
          createdAt: old?.createdAt ?? new Date().toISOString(),
        };
        if (old) products.splice(products.indexOf(old), 1, row);
        else products.unshift(row);
        audit("product", row.id, reason);
        return row;
      });
    },
    saveBinding: async (input) => {
      const v = c.assetBindingInputSchema.parse(input);
      const apps = await getApps();
      if (!apps.some((a) => a.id === v.appId && a.status === "active"))
        throw new Error("应用不存在或已停用");
      return mutate(v.mutationId, v, () => {
        const old = bindings.find(
          (b) => b.appId === v.appId && b.kind === v.kind,
        );
        if ((old?.version ?? 0) !== v.expectedVersion)
          throw new Error("关联已更新，请刷新后重试");
        const pool = pools.find(
          (p) => p.id === v.poolId && p.kind === v.kind && p.enabled,
        );
        if (v.poolId && !pool) throw new Error("权益空间类型不匹配");
        if (!v.acknowledgeScopeChange) throw new Error("请确认资产归属规则");
        const row: c.AssetBinding = {
          appId: v.appId,
          appName: apps.find((a) => a.id === v.appId)!.name,
          kind: v.kind,
          poolId: v.poolId,
          poolName: pool?.name ?? null,
          poolNameUg: pool?.nameUg ?? null,
          version: (old?.version ?? 0) + 1,
        };
        if (old) bindings.splice(bindings.indexOf(old), 1, row);
        else bindings.push(row);
        audit("binding", v.appId, v.reason);
        return row;
      });
    },
    adjust: async (input) => {
      if (data) {
        const v = c.assetAdjustmentSchema.parse(input);
        const apps = await getApps();
        if (!apps.some((a) => a.id === v.appId && a.status === "active")) throw new Error("应用不存在或已停用");
        return mutate(v.mutationId, v, () => {
          const result = data.adjust(v);
          audit("adjustment", `${v.poolId}:${v.userId}:${v.assetKey}`, v.reason);
          return result;
        });
      }
      throw new Error(
        "本地配置演示不执行资产入账，请连接测试数据库验收资产操作",
      );
    },
    refundExchange: async () => {
      throw new Error("本地配置演示不执行退款，请连接测试数据库验收退款");
    },
  };
}
